import React, { useEffect, useState } from 'react';
import { HTMLReactParserOptions } from 'html-react-parser';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { List } from 'src/components/custom';
import { Comments } from 'src/components/coreUI';
import { useAppDispatch, useAppSelector } from 'src/store';
import discussionActions, {
  selectDiscussionByContentId,
} from 'src/store/model/discussion';
import discussionReply from 'src/store/model/discussionReply';
import { useClassTopicContent, useConfirm } from 'src/hooks';
import { htmlParser } from 'src/libs/htmlParser';
import ChatBox from 'src/components/custom/Editor/ChatBox';
import {
  createDiscussion,
  createReply,
  deleteDiscussion,
  deleteReply,
  fetchDiscussions,
  updateDiscussion,
  updateReply,
} from 'src/thunks/discussion';
import { isAdminSelector } from 'src/utils/helper';

export type Props = {
  parseOption?: HTMLReactParserOptions;
  shouldScrollToItem?: boolean;
};

const DiscussionList: React.FC<Props> = ({
  parseOption,
  shouldScrollToItem,
}) => {
  const [editId, setEditId] = useState<string | undefined>();
  const [toggleComment, setToggleComment] = useState<string | undefined>();
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { class_topic_content_id, class_id, content } = useClassTopicContent();
  const discussions = useAppSelector((state) =>
    selectDiscussionByContentId(state, class_topic_content_id),
  );
  const currentUser = useAppSelector((state) => state.ui.app.me);
  const { Confirm, open } = useConfirm();
  const isAdmin = useAppSelector(isAdminSelector);

  const [showIds, setShowIds] = useState<string[]>([]);

  useEffect(() => {
    shouldScrollToItem && navigate(location.pathname, { state: undefined });
  }, []);

  useEffect(() => {
    if (!shouldScrollToItem) return;
    if (location.state) {
      const { parent_id, discussion_id } = location.state as any;
      const element = document.getElementById(parent_id || discussion_id);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.state]);

  return (
    <Comments.Group>
      <List
        columns={1}
        data={discussions}
        renderItem={(d) => (
          <Comments
            id={d.id}
            showReaction
            error={d.error}
            onError={() => {
              if (d.id === 'placeholder') {
                dispatch(createDiscussion(d));
              } else {
                dispatch(updateDiscussion(d));
              }
            }}
            text={
              editId === d.id ? (
                <ChatBox
                  html={d.message || ''}
                  onEsc={() => setEditId(undefined)}
                  onSend={(html) => {
                    dispatch(
                      discussionActions.updateEntity({ ...d, message: html }),
                    );
                    dispatch(updateDiscussion({ ...d, message: html }));
                    setEditId(undefined);
                  }}
                />
              ) : (
                htmlParser(d.message, parseOption)
              )
            }
            numberOfComments={d.replies?.length || 0}
            profileUrl={d.user?.profile_picture}
            name={d.user?.name}
            date={d.created_at}
            userId={d.user_id}
            onEdit={
              d.user_id === currentUser?.id ? () => setEditId(d.id) : undefined
            }
            onReply={() => {
              if (!showIds.includes(d.id)) {
                setShowIds([...showIds, d.id]);
              }
              setToggleComment((id) => (id === d.id ? undefined : d.id));
            }}
            onDelete={
              d.user_id === currentUser?.id || isAdmin
                ? () =>
                    open({
                      title: 'Confirm delete!',
                      content: 'Are you sure to delete this comment?',
                      async onOk() {
                        await dispatch(deleteDiscussion(d));
                      },
                    })
                : undefined
            }
          >
            {showIds.includes(d.id) ? (
              <React.Fragment>
                {d.replies?.map((r) => {
                  return (
                    <Comments
                      id={r.id}
                      error={r.error}
                      onError={() => {
                        if (r.id === 'placeholder') {
                          dispatch(createReply({ ...r }));
                        } else {
                          dispatch(updateReply(r));
                        }
                      }}
                      key={r.id}
                      text={
                        editId === r.id ? (
                          <ChatBox
                            html={r.message || ''}
                            onEsc={() => setEditId(undefined)}
                            onSend={async (html) => {
                              setEditId(undefined);

                              dispatch(
                                discussionReply.updateEntity({
                                  ...r,
                                  message: html,
                                }),
                              );

                              dispatch(updateReply({ ...r, message: html }));
                            }}
                          />
                        ) : (
                          htmlParser(r.message, parseOption)
                        )
                      }
                      profileUrl={r.user?.profile_picture}
                      name={r.user?.name}
                      date={r.created_at}
                      userId={r.user?.id}
                      onEdit={
                        r.user?.id === currentUser?.id
                          ? () => setEditId(r.id)
                          : undefined
                      }
                      onDelete={
                        r.user?.id === currentUser?.id || isAdmin
                          ? () =>
                              open({
                                title: 'Confirm delete!',
                                content: 'Are you sure to delete this comment?',
                                async onOk() {
                                  await dispatch(deleteReply(r));
                                },
                              })
                          : undefined
                      }
                    />
                  );
                })}

                {toggleComment === d.id && (
                  <Comments
                    profileUrl={currentUser?.profile_picture}
                    name={currentUser?.name}
                    text={
                      <ChatBox
                        shouldFocus
                        onEsc={() => setToggleComment(undefined)}
                        onSend={async (html) => {
                          setToggleComment(undefined);

                          const payload = {
                            parent_id: d.id,
                            message: html,
                            user: currentUser as Model.User,
                            user_id: currentUser?.id,
                            class_topic_content_id,
                            class_id,
                            class_topic_id: content.class_topic_id,
                          };

                          dispatch(discussionReply.createDummyReply(payload));
                          dispatch(createReply(payload));
                        }}
                      />
                    }
                  />
                )}

                {!!d.replies.length && (
                  <div
                    className="text-gray text-center cursor-pointer mt-6"
                    onClick={() => {
                      setShowIds(showIds.filter((id) => id !== d.id));
                    }}
                  >
                    Hide
                  </div>
                )}
              </React.Fragment>
            ) : (
              !!d.replies.length && (
                <div
                  className="text-gray text-center cursor-pointer mt-6"
                  onClick={() => {
                    setShowIds([...showIds, d.id]);
                  }}
                >
                  Show more
                </div>
              )
            )}
          </Comments>
        )}
      />

      <LoadMoreDiscuss
        class_id={class_id}
        class_topic_id={content.class_topic_id}
        class_topic_content_id={class_topic_content_id}
      />
      <Confirm />
    </Comments.Group>
  );
};

type LoadMoreProps = {
  class_topic_content_id?: string;
  class_topic_id?: string;
  class_id?: string;
};

export const LoadMoreDiscuss: React.FC<LoadMoreProps> = ({
  class_topic_content_id,
  class_id,
  class_topic_id,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const pagination = useAppSelector(
    (state) => state.model.discussion.pagination,
  );
  const dispatch = useAppDispatch();

  return pagination.next_page ? (
    <div
      className="text-gray text-center border-bottom mt-24 cursor-pointer py-8"
      onClick={async () => {
        setLoading(true);
        await dispatch(
          fetchDiscussions({
            class_topic_id,
            class_id,
            class_topic_content_id,
            page: pagination.next_page!,
          }),
        );
        setLoading(false);
      }}
    >
      {loading && <Icon loading name="spinner" />} Load more comments
    </div>
  ) : null;
};

export default DiscussionList;
